import { useCallback, useState } from 'react';
import type { Person } from '../types/people';
import AvatarStack from './AvatarStack';

interface AssigneeMultiPickerProps {
  people: Person[];
  value: string[];
  onChange: (personIds: string[]) => void;
  label?: string;
}

export default function AssigneeMultiPicker({ people, value, onChange, label }: AssigneeMultiPickerProps) {
  const [open, setOpen] = useState(false);

  const togglePerson = useCallback(
    (id: string) => {
      onChange(value.includes(id) ? value.filter((pid) => pid !== id) : [...value, id]);
    },
    [value, onChange]
  );

  const assignedIds = value;

  return (
    <div style={{ position: 'relative' }}>
      {label && (
        <label
          style={{
            display: 'block',
            marginBottom: '8px',
            fontSize: '13px',
            fontWeight: 600,
          }}
        >
          {label}
        </label>
      )}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '8px 10px',
          borderRadius: 'var(--radius)',
          border: '1px solid var(--color-border)',
          background: 'white',
          cursor: 'pointer',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AvatarStack people={people} personIds={assignedIds} size={24} />
          <span style={{ fontSize: '13px', color: 'var(--color-text-secondary)' }}>
            {assignedIds.length === 0 ? 'Assign people' : `${assignedIds.length} selected`}
          </span>
        </div>
        <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>▼</span>
      </button>
      {open && (
        <>
          <div
            role="presentation"
            style={{ position: 'fixed', inset: 0, zIndex: 10 }}
            onClick={() => setOpen(false)}
          />
          <div
            style={{
              position: 'absolute',
              right: 0,
              top: '100%',
              marginTop: '4px',
              zIndex: 11,
              minWidth: '240px',
              maxHeight: '260px',
              overflowY: 'auto',
              padding: '10px 12px',
              background: 'white',
              borderRadius: 'var(--radius)',
              border: '1px solid var(--color-border)',
              boxShadow: '0 4px 12px rgba(15, 23, 42, 0.16)',
            }}
          >
            <div
              style={{
                fontSize: '12px',
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                marginBottom: '8px',
              }}
            >
              Assign people
            </div>
            {people.length === 0 ? (
              <div style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>No people</div>
            ) : (
              people.map((person) => {
                const checked = value.includes(person.id);
                return (
                  <div
                    key={person.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => togglePerson(person.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        togglePerson(person.id);
                      }
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px 4px',
                      cursor: 'pointer',
                      fontSize: '13px',
                      borderRadius: 'var(--radius)',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      readOnly
                      tabIndex={-1}
                      style={{ pointerEvents: 'none', flexShrink: 0 }}
                    />
                    <span>{person.name}</span>
                  </div>
                );
              })
            )}
          </div>
        </>
      )}
    </div>
  );
}

